/**
 * RevivalSkillEffect — Conjuration Bind path Fork 2 active: reactive familiar revival.
 *
 * When the familiar dies, a revival window opens (4 seconds baseline). During
 * this window, activating Revival re-spawns the familiar at the death position
 * with 35% of its max HP — no cooldown consumed. If the window expires unused,
 * the familiar is gone until re-summoned via Bind/Empower.
 *
 * Outside a death window, Revival does nothing — it is purely reactive.
 *
 * Upgrade hooks (resolved here, values come from UpgradeEffectResolver):
 *   - Persistent (revival_upgrade_1): revivalWindowMs max (7000), revivalHpPercent max (0.5)
 *   - Surge (revival_upgrade_2): postRevivalDamageBonus additive (0.3),
 *     postRevivalAggroMultiplier additive (2.0), postRevivalDurationMs max (8000)
 *
 * The post-revival buff (Surge) is resolved at revival time and exposed via
 * getters for the integration layer to apply as a timed buff on the familiar.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Revival
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { CompanionSummonConfig } from './BindSkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RevivalPhase = 'idle' | 'window_open';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RevivalDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getMinEffect(skillId: string, key: string): number | undefined;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly summons: {
    spawn(config: CompanionSummonConfig, x: number, y: number): string;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface RevivalSkillConfig {
  /** Default revival window duration in ms (before upgrade overrides). */
  readonly revivalWindowMs: number;
  /** Default revival HP fraction (before upgrade overrides). */
  readonly revivalHpPercent: number;
}

const DEFAULT_CONFIG: RevivalSkillConfig = {
  revivalWindowMs: 4000,     // spec: 4 second revival window
  revivalHpPercent: 0.35,    // spec: 35% of familiar's max HP
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RevivalSkillEffect implements SkillEffect {
  readonly skillId = 'revival';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RevivalDeps;
  private readonly config: RevivalSkillConfig;

  private _phase: RevivalPhase = 'idle';
  private _windowRemaining = 0;
  private _windowDuration = 0;
  private _deathX = 0;
  private _deathY = 0;
  private _familiarMaxHp = 0;
  private _lastConfig: CompanionSummonConfig | null = null;
  private _familiarAlive = false;
  private _revivalCount = 0;
  private _lastRevivedId = '';

  // Post-revival buff state (Surge upgrade) — integration layer reads these
  private _postRevivalBuffActive = false;
  private _postRevivalDamageBonus = 0;
  private _postRevivalAggroMultiplier = 0;
  private _postRevivalBuffRemaining = 0;

  constructor(deps: RevivalDeps, config: Partial<RevivalSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return 0; }  // Revival itself has no cooldown
  get phase(): RevivalPhase { return this._phase; }
  get windowRemaining(): number { return this._windowRemaining; }
  get windowDuration(): number { return this._windowDuration; }
  get familiarAlive(): boolean { return this._familiarAlive; }
  get revivalCount(): number { return this._revivalCount; }
  get lastRevivedId(): string { return this._lastRevivedId; }
  get deathX(): number { return this._deathX; }
  get deathY(): number { return this._deathY; }
  get familiarMaxHp(): number { return this._familiarMaxHp; }
  get postRevivalBuffActive(): boolean { return this._postRevivalBuffActive; }
  get postRevivalDamageBonus(): number { return this._postRevivalDamageBonus; }
  get postRevivalAggroMultiplier(): number { return this._postRevivalAggroMultiplier; }
  get postRevivalBuffRemaining(): number { return this._postRevivalBuffRemaining; }

  // --- Integration layer hooks ---

  /**
   * Called by the integration layer when the companion familiar dies.
   * Opens the revival window at the death position with the familiar's
   * max HP and last spawn config stored for the potential re-spawn.
   *
   * The window duration and revival HP fraction are resolved from the
   * upgrade resolver at this moment — not re-evaluated per frame.
   */
  onFamiliarDied(
    deathX: number,
    deathY: number,
    maxHp: number,
    lastConfig: CompanionSummonConfig,
  ): void {
    this._familiarAlive = false;
    this._deathX = deathX;
    this._deathY = deathY;
    this._familiarMaxHp = maxHp;
    this._lastConfig = lastConfig;

    // Resolve window duration — Persistent override via 'max' aggregation
    const windowMs = this.deps.resolver.getMaxEffect('revival', 'revivalWindowMs')
      ?? this.config.revivalWindowMs;

    this._phase = 'window_open';
    this._windowRemaining = windowMs;
    this._windowDuration = windowMs;
  }

  /**
   * Called by the integration layer when a companion is freshly summoned
   * (by Bind, Empower, or any trunk-path summon skill). Resets Revival
   * to idle — the familiar is alive, no revival window needed.
   */
  onFamiliarSpawned(): void {
    this._familiarAlive = true;
    this._phase = 'idle';
    this._windowRemaining = 0;
    this._lastConfig = null;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'window_open'
      && this._windowRemaining > 0
      && this._lastConfig !== null;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve revival HP fraction — Persistent override via 'max' aggregation
    const hpPercent = this.deps.resolver.getMaxEffect('revival', 'revivalHpPercent')
      ?? this.config.revivalHpPercent;

    const revivalHp = Math.round(this._familiarMaxHp * hpPercent);

    // Re-spawn with the same config as the original summon, but adjusted HP
    const revivalConfig: CompanionSummonConfig = {
      ...this._lastConfig!,
      baseHp: revivalHp,
    };

    this._lastRevivedId = this.deps.summons.spawn(
      revivalConfig,
      this._deathX,
      this._deathY,
    );

    this._familiarAlive = true;
    this._phase = 'idle';
    this._windowRemaining = 0;
    this._revivalCount++;

    // Resolve post-revival buff (Surge upgrade)
    const damageBonus = this.deps.resolver.getAdditiveEffect('revival', 'postRevivalDamageBonus');
    const aggroMultiplier = this.deps.resolver.getMultiplicativeEffect('revival', 'postRevivalAggroMultiplier');
    const buffDuration = this.deps.resolver.getAdditiveEffect('revival', 'postRevivalDurationMs');

    if (damageBonus > 0 && buffDuration > 0) {
      this._postRevivalBuffActive = true;
      this._postRevivalDamageBonus = damageBonus;
      this._postRevivalAggroMultiplier = aggroMultiplier;
      this._postRevivalBuffRemaining = buffDuration;
    } else {
      this._postRevivalBuffActive = false;
      this._postRevivalDamageBonus = 0;
      this._postRevivalAggroMultiplier = 0;
      this._postRevivalBuffRemaining = 0;
    }

    // No cooldown consumed — revival is free within the window
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    // Tick the revival window countdown
    if (this._phase === 'window_open' && this._windowRemaining > 0) {
      this._windowRemaining = Math.max(0, this._windowRemaining - delta);
      if (this._windowRemaining <= 0) {
        // Window expired — familiar is gone, reset to idle
        this._phase = 'idle';
        this._lastConfig = null;
      }
    }

    // Tick the post-revival buff (Surge)
    if (this._postRevivalBuffActive && this._postRevivalBuffRemaining > 0) {
      this._postRevivalBuffRemaining = Math.max(0, this._postRevivalBuffRemaining - delta);
      if (this._postRevivalBuffRemaining <= 0) {
        this._postRevivalBuffActive = false;
        this._postRevivalDamageBonus = 0;
        this._postRevivalAggroMultiplier = 0;
      }
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._windowRemaining = 0;
    this._lastConfig = null;
    this._familiarAlive = false;
    this._postRevivalBuffActive = false;
    this._postRevivalDamageBonus = 0;
    this._postRevivalAggroMultiplier = 0;
    this._postRevivalBuffRemaining = 0;
  }
}
